/* ===========================================================
   ReviewForm — lets a logged-in user post (or update) a review.
   Anonymous users see a "Sign in to review" CTA instead.
   =========================================================== */
import React from "react";
import { Icons } from "@/lib/icons";
import { toast } from "@/components/shell";
import { useCreateReview, useMe } from "@/api/hooks";

function StarPicker({ value, onChange, size = 26 }) {
  const gold = "#f5b50a";
  return (
    <div role="radiogroup" aria-label="Rating" style={{ display: "inline-flex", gap: 6 }}>
      {[1, 2, 3, 4, 5].map((i) => (
        <button
          key={i}
          type="button"
          role="radio"
          aria-checked={value === i}
          onClick={() => onChange(i)}
          style={{
            background: "transparent", border: "none", padding: 0,
            cursor: "pointer", lineHeight: 0,
          }}
        >
          <svg width={size} height={size} viewBox="0 0 20 20">
            <path
              d="M10 1.5l2.6 5.3 5.9.9-4.2 4.1 1 5.8L10 14.9 4.7 17.6l1-5.8L1.5 7.7l5.9-.9z"
              fill={i <= value ? gold : "none"}
              stroke={i <= value ? gold : "#d6d2c5"}
              strokeWidth="1.5"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      ))}
    </div>
  );
}

function ReviewForm({ slug, onSubmitted }) {
  const { data: me } = useMe();
  const mutation = useCreateReview(slug);
  const [open, setOpen] = React.useState(false);
  const [rating, setRating] = React.useState(5);
  const [title, setTitle] = React.useState("");
  const [body, setBody] = React.useState("");

  if (!me) {
    return (
      <a className="btn btn-outline btn-sm" href="/pages/auth/login.html" style={{ marginTop: 12 }}>
        <Icons.User size={13} stroke={1.7} /> Sign in to review
      </a>
    );
  }

  if (!open) {
    return (
      <button
        type="button"
        className="btn btn-outline btn-sm"
        style={{ marginTop: 12 }}
        onClick={() => setOpen(true)}
      >
        <Icons.Plus size={13} stroke={2} /> Write a review
      </button>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (rating < 1 || rating > 5) {
      toast && toast("Pick a rating between 1 and 5");
      return;
    }
    try {
      await mutation.mutateAsync({
        rating,
        title: title.trim() || null,
        body: body.trim() || null,
      });
      toast && toast("Review saved");
      setOpen(false);
      setTitle("");
      setBody("");
      onSubmitted && onSubmitted();
    } catch (err) {
      toast && toast(err?.message || "Couldn't save review", "error");
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="review-form"
      style={{
        marginTop: 16, padding: 16, background: "var(--paper-2)",
        border: "1px solid var(--line)", borderRadius: 12,
        display: "flex", flexDirection: "column", gap: 12,
      }}
    >
      <div>
        <div className="kicker" style={{ marginBottom: 6 }}>Your rating</div>
        <StarPicker value={rating} onChange={setRating} />
      </div>
      <label>
        <div className="kicker" style={{ marginBottom: 4 }}>Title (optional)</div>
        <input
          className="input"
          maxLength={120}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="One-line summary of your experience"
        />
      </label>
      <label>
        <div className="kicker" style={{ marginBottom: 4 }}>Details (optional)</div>
        <textarea
          className="textarea"
          rows={4}
          maxLength={4000}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder="What worked well or didn't? Other clinicians and retailers find this useful."
        />
      </label>
      <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
        <button type="button" className="btn btn-ghost btn-sm" onClick={() => setOpen(false)}>
          Cancel
        </button>
        <button type="submit" className="btn btn-primary btn-sm" disabled={mutation.isPending}>
          {mutation.isPending ? "Saving…" : "Submit review"}
        </button>
      </div>
    </form>
  );
}

export { ReviewForm };
